import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatCurrency, formatDate, shortId } from '../lib/utils';

export default function SettlementDetailPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { settlementId } = useParams();
  const [bankAmount, setBankAmount] = useState('');

  const q = useQuery({
    queryKey: ['settlements', 'detail', settlementId],
    queryFn: async () => (await api.get(`/settlements/${settlementId}`)).data,
  });

  const transferMutation = useMutation({
    mutationFn: async (amount) =>
      (await api.post('/settlements/transfer', {
        settlement_id: settlementId,
        bank_deposit_amount: parseFloat(amount),
      })).data,
    onSuccess: (data) => {
      if ((data.alerts || []).length > 0) toast.warning(`Variance: ${data.alerts.join(', ')}`);
      else toast.success('Transferencia registrada');
      qc.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: () => toast.error('Error registrando transferencia'),
  });

  if (q.isLoading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (!q.data) return <div className="text-sm text-muted-foreground">{t('common.empty')}</div>;

  const { settlement, lines, journal_entry, transfer, payments } = q.data;

  return (
    <div data-testid="settlement-detail-page">
      <PageHeader
        title={settlement.tiktok_settlement_id}
        subtitle={`${t('settlements.column_date')}: ${formatDate(settlement.settlement_date, locale)}`}
        actions={
          <Button variant="secondary" size="sm"
            onClick={() => navigate('/settlements')}
            data-testid="settlement-back-button">
            <ArrowLeft className="h-4 w-4 mr-2" /> {t('common.back')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-6">
        <MiniStat label={t('settlements.column_payments')}
          value={formatCurrency(settlement.payments_total, locale)} />
        <MiniStat label={t('settlements.column_fees')}
          value={formatCurrency(
            Object.values(settlement.fee_totals || {}).reduce((a, b) => a + b, 0), locale
          )} />
        <MiniStat label={t('settlements.column_refunds')}
          value={formatCurrency(settlement.refunds_total, locale)} />
        <MiniStat label={t('settlements.column_payout')}
          value={formatCurrency(settlement.expected_payout, locale)}
          extra={<StatusBadge status={settlement.status || 'pending'} />} />
      </div>

      <Tabs defaultValue="summary" data-testid="settlement-detail-tabs">
        <TabsList>
          <TabsTrigger value="summary">{t('settlements.detail_tabs_summary')}</TabsTrigger>
          <TabsTrigger value="fees">{t('settlements.detail_tabs_fees')}</TabsTrigger>
          <TabsTrigger value="refunds">{t('settlements.detail_tabs_refunds')}</TabsTrigger>
          <TabsTrigger value="je">{t('settlements.detail_tabs_je')}</TabsTrigger>
          <TabsTrigger value="transfer">{t('settlements.detail_tabs_transfer')}</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-base">Payments</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>QBO Invoice</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(payments || []).map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{shortId(p.related_order_id, 10)}</TableCell>
                      <TableCell className="font-mono text-xs">{shortId(p.invoice_id)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(p.amount, locale)}</TableCell>
                    </TableRow>
                  ))}
                  {(!payments || payments.length === 0) && (
                    <TableRow><TableCell colSpan={3} className="text-center text-xs text-muted-foreground py-6">{t('common.empty')}</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="fees">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-base">Fees by concept</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.type')}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(settlement.fee_totals || {}).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell className="text-xs">{k}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(v, locale)}</TableCell>
                    </TableRow>
                  ))}
                  {settlement.uncategorized_total > 0 && (
                    <TableRow className="bg-[hsl(var(--status-warning-bg))]">
                      <TableCell className="text-xs font-medium">uncategorized</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(settlement.uncategorized_total, locale)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="refunds">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-base">Refunds</CardTitle></CardHeader>
            <CardContent>
              <div className="text-sm">
                {t('common.total')}: <span className="tabular-nums">{formatCurrency(settlement.refunds_total, locale)}</span>
              </div>
              <Table className="mt-3">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.type')}</TableHead>
                    <TableHead className="text-right">{t('common.amount')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(lines || []).filter((l) => l.charge_type === 'refund_amount').map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{l.charge_type}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(l.amount, locale)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="je">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base">
                Journal Entry {journal_entry?.qbo_je_id ? `· ${shortId(journal_entry.qbo_je_id)}` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {journal_entry ? (
                <Table data-testid="settlement-je-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(journal_entry.debits || []).map((d, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm">{d.account}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(d.debit, locale)}</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-medium">
                      <TableCell className="text-sm">{journal_entry.credit?.account}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(journal_entry.credit?.credit, locale)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              ) : <div className="text-sm text-muted-foreground">{t('common.empty')}</div>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="transfer">
          <Card className="bg-card border-border">
            <CardHeader><CardTitle className="text-base">{t('settlements.record_transfer')}</CardTitle></CardHeader>
            <CardContent>
              {transfer ? (
                <div className="text-sm space-y-1" data-testid="settlement-transfer-info">
                  <div>id: <span className="font-mono">{shortId(transfer.qbo_transfer_id)}</span></div>
                  <div>expected: {formatCurrency(transfer.expected_amount, locale)}</div>
                  <div>actual: {formatCurrency(transfer.actual_amount, locale)}</div>
                  <div>variance: {formatCurrency(transfer.variance, locale)}</div>
                  {(transfer.alerts || []).map((a, i) => (
                    <div key={i} className="text-xs text-[hsl(var(--status-danger))]">{a}</div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-3 max-w-md">
                  <Label htmlFor="bank">{t('settlements.bank_amount')}</Label>
                  <Input
                    id="bank"
                    type="number"
                    step="0.01"
                    value={bankAmount}
                    onChange={(e) => setBankAmount(e.target.value)}
                    placeholder={`expected ${settlement.expected_payout}`}
                    data-testid="settlement-bank-amount-input"
                  />
                  <Button
                    onClick={() => transferMutation.mutate(bankAmount || settlement.expected_payout)}
                    disabled={transferMutation.isPending}
                    data-testid="settlement-record-transfer-button"
                  >
                    {transferMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('settlements.record_transfer')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MiniStat({ label, value, extra }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
        {extra && <div className="mt-2">{extra}</div>}
      </CardContent>
    </Card>
  );
}
